/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 27/01/22
 */

import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { WorkplanSupervisor } from '@/wpt-lib/interfaces/Supervisor';

export interface WorkplanVersion {
    id: string | number;
    createdAt: string;
    version: number;
    state: WorkplanState;
    stage: WorkplanStage;
    workplan: Workplan;
    supervisors: WorkplanSupervisor[];

    fte: number;
    workloadCategory: string | null;
    hasCustomWorkloadPortions: boolean;
    teachingWorkloadPortion: number;
    researchWorkloadPortion: number;
    serviceWorkloadPortion: number;
    seniorStaffWorkloadPortion: number;
    professionalStaffWorkloadPortion: number;
    staffType: WorkloadStaffType;

    probationPlan: boolean;
    probationPeriod: string | null;
    probationUnit: string | null;
    probationEndDate: string | null;

    gradeName: string | null;

    planningMeetingDate: string | null;
    progressReviewMeetingDate: string | null;
    endOfYearMeetingDate: string | null;

    overallEndOfYearRating: number | null;
    teachingEndOfYearRating: number | null;
    researchEndOfYearRating: number | null;
    serviceEndOfYearRating: number | null;
    isFinalProbation: boolean;

    plannedLeaveDeclaration: boolean | null;
    conflictOfInterestDeclaration: boolean | null;
    mandatoryTrainingDeclaration: boolean | null;
}

export interface Workplan {
    id: string | number;
    createdAt: string;
    year: number;
    currentVersion: Omit<WorkplanVersion, 'workplan'>;
    state: WorkplanState;
    assignment?: Assignment;

    activityFormTemplateId?: number;
    goalFormTemplateId?: number;
    leaveFormTemplateId?: number;
    trainingFormTemplateId?: number;
    considerationFormTemplateId?: number;
    behaviouralFormTemplateId?: number;
}

export enum WorkplanState {
    DRAFT = 'DRAFT',
    SUBMITTED = 'SUBMITTED',
    IN_PROGRESS = 'IN_PROGRESS',
    REVIEW = 'REVIEW',
    INFORMATION_REQUESTED = 'INFORMATION_REQUESTED',
    MODERATION = 'MODERATION',
    ENDORSED = 'ENDORSED',
    HISTORICAL = 'HISTORICAL',
}

export enum WorkplanStage {
    PLANNING = 'PLANNING',
    PART_YEAR = 'PART_YEAR',
    END_YEAR = 'END_YEAR',
    CLOSED = 'CLOSED',
}

export enum WorkloadStaffType {
    ACADEMIC = 'ACADEMIC',
    PROFESSIONAL = 'PROFESSIONAL',
    SENIOR = 'SENIOR',
}

export const mapWorkloadStaffTypeToString: Record<WorkloadStaffType, string> = {
    [WorkloadStaffType.ACADEMIC]: 'Academic',
    [WorkloadStaffType.PROFESSIONAL]: 'Professional',
    [WorkloadStaffType.SENIOR]: 'Senior Staff',
};

export interface WorkplanSummary {
    counts: {
        personalGoals: number;
        activities: number;
        healthAndSafetyTraining: number;
        bookedAnnualLeaveDays: number;
        plannedOtherLeaveDays: number;
        bookedOtherLeaveDays: number;
        plannedAnnualDays: number;
    };
    benchmarks: {
        teaching: {
            [expectation: string]: number;
        };
        research: {
            [expectation: string]: number;
        };
        service: {
            [expectation: string]: number;
        };
    };
    strategy: {
        [strategyItem: string]: number;
    };
    goalTypes: {
        [goal: string]: number;
    };
}

export interface WorkplanAttachment {
    id: number;
    createdAt: string;
    updatedAt: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    filePath: string;
    attachmentType: WorkplanAttachmentType;
    author?: Assignment;
}

export enum WorkplanAttachmentType {
    SUPPORTING_DOCUMENT = 'SUPPORTING_DOCUMENT',
    EOY_WORKPLAN_COMMENTS_AND_RATING = 'EOY_WORKPLAN_COMMENTS_AND_RATING',
    WORKPLAN = 'WORKPLAN',
}

export interface WorkplanYear {
    id: number;
    year: number;
    historical: boolean;
    createdAt: string;
    versions: number[];
    states: number[];
}

export interface WorkplanStateHistory {
    createdAt: string;
    from: WorkplanState;
    to: WorkplanState;
    fromStage?: WorkplanStage;
    toStage?: WorkplanStage;
    version: number;
    assignment: Assignment;
}

export interface UpdateWorkplanEOYRatingDto {
    endOfYearRating: {
        teachingEndOfYearRating: number | null;
        researchEndOfYearRating: number | null;
        serviceEndOfYearRating: number | null;
        overallEndOfYearRating: number | null;
        isFinalProbation: boolean;
    };
}

export interface UpdateWorkplanStateDto {
    state: WorkplanState;
    stage?: WorkplanStage;
    acknowledged?: boolean;
}

export interface UpdateMultipleWorkplansDto {
    id: Workplan['id'];
    assignmentId?: Assignment['id'];
    update: UpdateWorkplanStateDto | UpdateWorkplanEOYRatingDto;
}

export interface WorkplanMeetingDatesDto {
    planningMeetingDate: string | null;
    progressReviewMeetingDate: string | null;
    endOfYearMeetingDate: string | null;
}

export interface UpdateWorkplanMeetingDatesDto {
    meetingDates: WorkplanMeetingDatesDto;
}

export interface UpdateProbationDto {
    probation: {
        probationPlan: boolean;
    };
}

export interface UpdateDeclarationsDto {
    declarations: {
        plannedLeaveDeclaration?: boolean;
        conflictOfInterestDeclaration?: boolean;
        mandatoryTrainingDeclaration?: boolean;
    };
}

export interface BulkActionResponse {
    updatedWorkplans: PromiseFulfilledResult<Workplan>[];
    rejectedWorkplans: ({
        id: Assignment['id'] | Workplan['id']; // for submitted workplans and staff workplans
    } & PromiseRejectedResult['reason'])[];
}

export const mapWorkplanStateToString: Record<WorkplanState, string> = {
    [WorkplanState.DRAFT]: 'Draft',
    [WorkplanState.SUBMITTED]: 'Submitted',
    [WorkplanState.IN_PROGRESS]: 'In Progress',
    [WorkplanState.REVIEW]: 'Review',
    [WorkplanState.INFORMATION_REQUESTED]: 'Information Requested',
    [WorkplanState.MODERATION]: 'Moderation',
    [WorkplanState.ENDORSED]: 'Endorsed',
    [WorkplanState.HISTORICAL]: 'Historical',
};

export const mapWorkplanStageToString: Record<WorkplanStage, string> = {
    [WorkplanStage.PLANNING]: 'Planning',
    [WorkplanStage.PART_YEAR]: 'Part Year',
    [WorkplanStage.END_YEAR]: 'End Year',
    [WorkplanStage.CLOSED]: 'Closed',
};

export const mapWorkplanAttachmentTypeToString: Record<WorkplanAttachmentType, string> = {
    [WorkplanAttachmentType.SUPPORTING_DOCUMENT]: 'Supporting Document',
    [WorkplanAttachmentType.EOY_WORKPLAN_COMMENTS_AND_RATING]: 'End of Year workplan comments and rating',
    [WorkplanAttachmentType.WORKPLAN]: 'Workplan',
};

export const historyStateComments: Record<WorkplanState, { [key in WorkplanState]?: string }> = {
    [WorkplanState.DRAFT]: {
        [WorkplanState.SUBMITTED]: 'Submitted workplan draft %version% for review.',
        [WorkplanState.HISTORICAL]: 'Workplan %version% draft changed to historical.',
    },
    [WorkplanState.SUBMITTED]: {
        [WorkplanState.DRAFT]: 'Requested further information in workplan draft %version%.',
        [WorkplanState.IN_PROGRESS]: 'Approved workplan draft %version%.',
        [WorkplanState.HISTORICAL]: 'Workplan %version% draft is changed to historical.',
    },
    [WorkplanState.IN_PROGRESS]: {
        [WorkplanState.REVIEW]: 'Submitted workplan %version% for review.',
        [WorkplanState.HISTORICAL]: 'Workplan %version% is changed to historical.',
    },
    [WorkplanState.REVIEW]: {
        [WorkplanState.IN_PROGRESS]: 'Approved changes in workplan %version%.',
        [WorkplanState.INFORMATION_REQUESTED]: 'Requested further information in workplan %version%.',
        [WorkplanState.MODERATION]: 'Workplan %version% has gone to moderation.',
        [WorkplanState.ENDORSED]: 'Endorsed workplan %version%.',
        [WorkplanState.HISTORICAL]: 'Workplan %version% is changed to historical.',
    },
    [WorkplanState.INFORMATION_REQUESTED]: {
        [WorkplanState.REVIEW]: 'Submitted workplan %version% for review.',
        [WorkplanState.HISTORICAL]: 'Workplan %version% is changed to historical',
    },
    [WorkplanState.MODERATION]: {
        [WorkplanState.ENDORSED]: 'Endorsed workplan %version%.',
        [WorkplanState.HISTORICAL]: 'Workplan %version% is changed to historical.',
    },
    [WorkplanState.ENDORSED]: {
        [WorkplanState.REVIEW]: 'Unendorsed workplan %version%.',
        [WorkplanState.HISTORICAL]: 'Workplan %version% is changed to historical.',
    },
    [WorkplanState.HISTORICAL]: {},
};

export function generateHistoryComment(history: WorkplanStateHistory): string {
    let comment;
    if (historyStateComments[history.from][history.to])
        comment = historyStateComments[history.from][history.to] as string;
    else
        comment = `Changed workplan %version% state from ${mapWorkplanStateToString[history.from]} to ${
            mapWorkplanStateToString[history.to]
        }.`;

    return comment.replace('%version%', `version ${history.version}`);
}

export const StateToStage: Record<WorkplanState, WorkplanStage | WorkplanStage[]> = {
    [WorkplanState.DRAFT]: WorkplanStage.PLANNING,
    [WorkplanState.SUBMITTED]: WorkplanStage.PLANNING,
    [WorkplanState.IN_PROGRESS]: [WorkplanStage.PART_YEAR, WorkplanStage.END_YEAR],
    [WorkplanState.REVIEW]: [WorkplanStage.PART_YEAR, WorkplanStage.END_YEAR],
    [WorkplanState.INFORMATION_REQUESTED]: [WorkplanStage.PART_YEAR, WorkplanStage.END_YEAR],
    [WorkplanState.MODERATION]: WorkplanStage.END_YEAR,
    [WorkplanState.ENDORSED]: WorkplanStage.CLOSED,
    [WorkplanState.HISTORICAL]: WorkplanStage.CLOSED,
};

export const workplanStageHelpText = {
    [WorkplanStage.PART_YEAR]: 'this will enable the workplan to be reviewed any time in the year',
    [WorkplanStage.END_YEAR]: 'this will enable the end of year comments and ratings to be entered',
    [WorkplanState.ENDORSED]:
        'this will complete the workplan review process. The workplan will be saved in Personnel File (inside Content Manager/Trim). Staff can view feedback and ratings.',
    [WorkplanState.MODERATION]:
        'for areas that have a moderation process, select this stage to have this reviewed by the moderation panel prior to endorsement',
};
